<template>
    <div>
        <v-card elevation="1">
            <v-card-text class="pb-1">
                <div class="caption mb-4">
                    <p>
                        Enter the date of your last required periodic filing of a good standing document with the Colorado Secretary
                        of State.  Provide an image of your last periodic report, if available.  For more information,
                        <a href='https://www.sos.state.co.us/pubs/business/FAQs/certGoodStanding.html' target="_blank">go here</a>.
                    </p>

                    <p>
                        If you aren't ready to establish your company with State authorities yet,
                        tap <span class="primary--text">SKIP</span> to bypass this required step temporarily, then
                        return and upload the periodic report as soon as possible. Elite Logistics may withhold driving assignments
                        until a valid periodic report is on file, at its sole discretion.
                    </p>

                    <v-form v-model="valid">
                        <v-date-picker
                            v-model="date"
                            color="primary"
                            class="my-6"
                            scrollable
                            full-width
                            :allowed-dates="allowedDates"
                            :rules="[rules.required]"
                            @input="formChange"
                        >
                        </v-date-picker>
                    </v-form>

                    <div class="my-8">
                        <input
                            ref="fileref"
                            type="file"
                            accept="image/*,.pdf"
                            @change="onFileChange($event)"
                            style="display: none"
                        />

                        <v-card
                            :class="evidence && evidence.url ? 'success--text' : 'grey--text'"
                            class="mx-auto mt-0 v-card--account"
                            outlined
                            @click="$refs.fileref.click()"
                            :key="updateKey"
                        >
                            <v-row>
                                <v-col>
                                    <v-img
                                        v-if="evidence && evidence.url && evidence.type == 'image'"
                                        :src="fileHandler.objectURL"
                                        contain
                                        width="100%"
                                        aspect-ratio="1"
                                    >
                                        <template v-slot:placeholder>
                                            <v-row class="fill-height ma-0" align="center" justify="center">
                                                <v-progress-circular
                                                    indeterminate
                                                    color="primary"
                                                ></v-progress-circular>
                                            </v-row>
                                        </template>
                                    </v-img>

                                    <div class="text-center" v-else-if="evidence && evidence.type != 'pdf'">
                                        <v-icon
                                            class="mx-auto mt-3"
                                            size="96"
                                        >
                                            mdi-file-image-outline
                                        </v-icon>

                                        <div class="mx-auto text-uppercase my-3" style="width: 60%; font-size: .6rem; line-height: 1rem">
                                            tap to open camera, or select image or pdf file
                                        </div>
                                    </div>

                                    <div class="text-center" v-else>
                                        <v-icon
                                            class="mx-auto mt-3"
                                            size="96"
                                        >
                                            mdi-checkbox-marked-circle-outline
                                        </v-icon>

                                        <div class="mx-auto text-uppercase my-3" style="width: 60%; font-size: .6rem; line-height: 1rem">
                                            pdf selected
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-card>
                        <v-row>
                            <v-col>
                                <div class="text-center">
                                    <div class="mt-3" v-if="evidence && evidence.url">
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    @click.stop="viewDoc(fileHandler)"
                                                >
                                                    <v-icon class="mr-2">mdi-image-search-outline</v-icon>
                                                    View
                                                </v-btn>
                                            </template>
                                            <span>View Document</span>
                                        </v-tooltip>

                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn
                                                    class="ml-2"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    @click.stop="clearDoc()"
                                                >
                                                    <v-icon class="mr-2">mdi-image-remove</v-icon>
                                                    Remove
                                                </v-btn>
                                            </template>
                                            <span>Remove Document</span>
                                        </v-tooltip>
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <not-saved-warning
            :dirty="dirty"
            message="NOT SAVED - make sure the filing date is correct and the image (if provided) is clear, then CLICK NEXT"
        ></not-saved-warning>

        <v-divider class="my-3" />

        <v-btn
            text
            @click="back"
        >
            Back
        </v-btn>

        <v-btn
            text
            color="primary"
            @click="next"
            :disabled="!date || !evidence.image"
        >
            Next
        </v-btn>

        <v-btn text color="primary" @click="skip"> Skip </v-btn>

        <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
                <v-icon
                    v-bind="attrs"
                    v-on="on"
                    x-small
                    color="red"
                    v-show="dirty"
                    class="ml-16"
                >mdi-pencil</v-icon>
            </template>
            Values Changed
        </v-tooltip>
    </div>
</template>

<script>
import complianceMixins from "../common/props"
import ServerFile from "@/plugins/ServerFile"

export default {
	mixins: [complianceMixins],

    data() {
        return {
            step: "02c340291ffc45b5888995237a4fca1f", // magic, never change!
            vals: null,
            filename: "",
            compdate: 0,
            valid: true,
            dirty: false,
            updateKey: 1,
            objectId: null,
            fileHandler: null,
            savedEvidence: null,
            date: null,
            evidence: {
                image: null,
                url: null
            },
            today: this.$time.local().startOf("day").plus({days: 1})
        }
    },

    methods: {
        formChange() {
            this.dirty = this.valid = true
        },

        allowedDates(val) {
            if (this.$time.fromISO(val).startOf("day") < this.today) {
                return true
            }
            return false
        },

        onFileChange(val) {
            //
            //  The first time the image changes we check to see if there is already an image defined
            //  on the server.  If so, we save the evidence off to the side so we can delete the old file
            //  and save the new one when this step is saved.  If we have already saved it to the side, we
            //  don't do this, because they may be saving images multiple times and we only care about the
            //  original.  Also, we only care if the evidence is stored on the server, so we test to see if
            //  if the evidence points to an http url.  If it isn't on the server, we don't care how many
            //  times the user swaps out the image.
            //
            if (!this.savedEvidence && (this.evidence && this.evidence.url && this.evidence.url.indexOf("blob") === -1)) {
                this.savedEvidence = this.store.vue.$clone(this.evidence)
                this.dirty = true
            }

            this.evidence.image = val.target.files[0]
            if (!this.evidence.image) {
                this.fileHandler.cleanup()
                this.evidence.type = ""
                this.evidence.url = ""
            }
            else {
                //
                //  For image files, we display a thumbnail of the image as a "blob".  For PDFs
                //  we just change the icon in the picker to a PDF icon.
                //
                this.fileHandler.loadLocalImage(this.evidence.image)
                this.evidence.url = this.fileHandler.url
                this.evidence.type = this.isImage(this.evidence.image) ? "image" : "pdf"
                this.dirty = true

                //
                //  Force a re-render of the <img>.  And indicate that the compliance item is modified.
                //
                this.updateKey++
                this.dirty = true
            }
        },

        clearDoc() {
            this.store.vue.$confirm("Delete this image?", {icon: "mdi-help", title: "Confirm", color: "primary"})
            .then((res) => {
                if (res) {
                    //
                    //  If this is an http link, it is pointing at an actual file on the server that we have
                    //  to delete and replace.  This will happen when the user clicks the Next button.  Here,
                    //  we save a reference to the file so it can be cleaned up.
                    //
                    if (!this.savedEvidence && this.fileHandler.isLoaded) {
                        this.savedEvidence = this.store.vue.$clone(this.evidence)
                        this.dirty = true
                    }

                    //
                    //  Clean out the evidence.  If there is no image on the server, savedEvidence is not defined, and
                    //  the user is deleting an image that hasn't been saved yet, restoring the item to its virgin
                    //  state.  So we can reset its changed flag to false.  Otherwise we show it as changed to make sure
                    //  the saved image is removed from the server.
                    //
                    this.dirty = this.savedEvidence ? true : false
                    this.fileHandler.cleanup()
                    this.evidence.url = ""
                    this.evidence.image = null
                    this.evidence.type = ""
                    this.$refs.fileref.value = ""
                    this.updateKey++
                }
            })
        },

        viewDoc() {
            if (this.fileHandler.isLoaded) {
                this.fileHandler.getUrlAsBlob()
                .then(blob => {
                    window.open(blob, "_blank")
                })
            }
        },

        back() {
            if (this.dirty) {
                this.$confirm(this.lostMessage, {icon: "mdi-help", title: "Confirm", color: "primary"})
                .then((res) => {
                    if (res) {
                        this.$emit("back")
                    }
                })
            }
            else {
                this.$emit("back")
            }
        },

        skip() {
            if (this.dirty) {
                this.$confirm(this.lostMessage, {
                    icon: "mdi-help",
                    title: "Confirm",
                    color: "primary",
                }).then((res) => {
                    if (res) {
                        this.$emit("next")
                    }
                })
            } else {
                this.$emit("next")
            }
        },

        async next() {
            const vm = this
            let fn

            if (this.dirty) {
                this.$emit("loading", true)

                if (this.isImage(this.evidence.image)) {
                    const maxSize = 600

                    //
                    //  All files get rezied into jpeg output.
                    //
                    fn = this.store.vue.$uuid.v1().replace(/-/g, "") + ".jpg"

                    //
                    //  Save it.
                    //
                    await this.fileHandler.saveImage(this.evidence.image, this.getCompliancePath(), fn, maxSize)
                }
                else {
                    //
                    //  It is a PDF, or other, file.
                    //
                    let ext = this.evidence.image.name.split(".").pop()
                    fn = this.store.vue.$uuid.v1().replace(/-/g, "") + (ext ? `.${ext}` : "")

                    //
                    //  The file would retain its original name unless we change the
                    //  guts of the browser's File object, which is done by overwriting
                    //  the File object's name property with a writable property and
                    //  assigning it the new name.
                    //
                    Object.defineProperty(this.evidence.image, "name", {
                        writable: true,
                        value: fn,
                    })

                    await this.fileHandler.saveFileObject(this.evidence.image, this.getCompliancePath(), fn)
                }

                const rec = {
                    step: this.step,
                    user: this.vals.userId,
                    compdate: this.store.vue.$time.local().toMillis(),
                    values: {
                        file: fn,
                        value: this.date
                    }
                }

                //
                //  Turns this save into an update.
                //
                if (this.objectId) {
                    rec.objectId = this.objectId
                }

                this.saveStepData(this.store.vue.$db, rec)
                .then(async () => {
                    if (vm.savedEvidence && vm.savedEvidence.url.indexOf("blob:") == -1) {
                        //
                        //  Delete the old file, but it is not the end of the world if it fails, there
                        //  is nothing we can do about it.
                        //
                        const args = { vue: vm, token: vm.vals.userToken, url: vm.savedEvidence.url }
                        const fileHandler = new ServerFile(args)

                        /*await*/ fileHandler.deleteUrl().catch(error => {
                            console.log(`*****  error deleting old step file: ${error} *****`)
                        })
                    }
                    vm.dirty = false
                    vm.$emit("next")
                })
                .catch(error => {
                    vm.$emit("error", error)
                })
                .finally(() => {
                    vm.$emit("loading", false)
                })
            }
            else {
                this.$emit("next")
            }
        }
    },

    beforeDestroy() {
        if (this.fileHandler) {
            this.fileHandler.cleanup()
        }
    },

    created() {
        const vm = this

        this.vals = this.getStoreValues(["userToken", "userId"])

        //
        //  Get our saved data.  Its just the file name, if any, and the date
        //  last signed, which we don't currently display.
        //
        this.getStepData(this.store.vue.$db, this.step, this.vals.userId)
        .then(data => {
            if (data.length === 1) {
                vm.objectId = data[0].objectId
                vm.evidence.url = vm.getComplianceUrl(data[0].values.file)
                vm.evidence.type = vm.isImage(data[0].values.file) ? "image" : "pdf"
                vm.date = data[0].values.value
            }

            //
            //  We create a file handler regardless of whether a file has already
            //  been saved.
            //
            const args = {
                vue: vm,
                token: this.vals.userToken,
                url: vm.evidence.url
            }
            this.fileHandler = new ServerFile(args)

            //
            //  If a file does exist on the server, load it so it is displayed.
            //
            if (vm.evidence.url) {
                vm.fileHandler.getUrlAsBlob()
                .then(() => {
                    this.evidence.image = this.fileHandler.blob
                })
                .catch(error => {
                    vm.$emit("error", `Error loading saved file - please notify your Elite contact: ${error}`)
                })
            }
        })
        .catch(error => {
            vm.$emit("error", error)
        })
        .finally(() => {
            vm.$emit("loading", false)
        })

    }
}
</script>